<script setup lang="ts">
import { useAccount } from "@/composables/account/useAccount";
import CnmcOutboundFilingDialog from "@/dashboard/inventory_management/send/cnmc/CnmcOutboundFilingDialog.vue";
import CnmcSendReviewModal from "@/dashboard/inventory_management/send/cnmc/CnmcSendReviewModal.vue";
import type { CnmcOutboundSend } from "@/dashboard/inventory_management/send/cnmc/useCnmcOutboundSend";
import CnmcFilingDetailsDialog from "@/dashboard/production_strategy/issuance-reporting/es/CnmcFilingDetailsDialog.vue";
import type {
  CnmcFilingProfile,
  OutboundFiling,
} from "@/dashboard/production_strategy/issuance-reporting/es/cnmcTypes";
import type { Company } from "@/types";
import { ref } from "vue";

/**
 * The dialogs of Send's CNMC mode: the review, the filing details it may need first, and the filing
 * card once the filing is prepared. Stays mounted while nothing is selected, so the filing card
 * outlives the selection the send cleared.
 */
const props = defineProps<{
  cnmc: CnmcOutboundSend;
  company: Company;
}>();

const emit = defineEmits<{
  /** A filing was prepared; Send clears what it filed. */
  sent: [];
  /** An action in the filing card reserved or released GOs. */
  filingChanged: [];
}>();

// Seeds the filing details with the signed-in account's contact.
const { accountData } = useAccount();

const createdFiling = ref<OutboundFiling | null>(null);
const showFilingDialog = ref(false);
const showProfileDialog = ref(false);
const isSavingProfile = ref(false);

const setReviewOpen = (open: boolean) => {
  if (!open) props.cnmc.closeReview();
};

const confirm = async () => {
  const filing = await props.cnmc.confirm();
  if (!filing) return;
  props.cnmc.closeReview();
  createdFiling.value = filing;
  showFilingDialog.value = true;
  emit("sent");
};

const openProfileDialog = () => {
  props.cnmc.closeReview();
  showProfileDialog.value = true;
};

const saveProfile = async (profile: CnmcFilingProfile) => {
  isSavingProfile.value = true;
  try {
    if (await props.cnmc.saveProfile(profile)) showProfileDialog.value = false;
  } finally {
    isSavingProfile.value = false;
  }
};

const updateFiling = (filing: OutboundFiling) => {
  createdFiling.value = filing;
  emit("filingChanged");
};
</script>

<template>
  <CnmcSendReviewModal
    :modelValue="cnmc.isReviewOpen"
    :preview="cnmc.preview"
    :notice="cnmc.notice"
    :isLoading="cnmc.isBusy"
    @update:modelValue="setReviewOpen"
    @confirm="confirm"
    @apply-suggestion="cnmc.applySuggestion"
    @complete-profile="openProfileDialog"
  />

  <CnmcFilingDetailsDialog
    v-if="cnmc.connectionId !== null"
    v-model="showProfileDialog"
    :profile="cnmc.connection?.profile ?? null"
    :company
    :account="accountData"
    :connectionId="cnmc.connectionId"
    :producerNif="cnmc.connection?.producer"
    :busy="isSavingProfile"
    @save="saveProfile"
  />

  <CnmcOutboundFilingDialog
    v-if="createdFiling"
    v-model="showFilingDialog"
    :companyId="company.id!"
    :filing="createdFiling"
    @update="updateFiling"
  />
</template>
